import { UUID } from '@/common/utils';
import { BUCKET_TYPES, INTERVAL_SELECT_OPTIONS } from './bucketAggregation.defines';
import { BucketAggregationFactory } from './BucketAggregationFactory';

export type BucketAggregationsType = (typeof BUCKET_TYPES)[number];

export abstract class BucketAggregation {
  id: UUID;

  type: BucketAggregationsType;

  show = true;

  constructor(id: UUID, type: BucketAggregationsType, show = true) {
    this.id = id;
    this.type = type;
    this.show = show;
  }

  abstract createClone(id: UUID): BucketAggregation;

  abstract isValid(): boolean;

  abstract configure(config: unknown): void;

  abstract changeType(type: BucketAggregationsType): BucketAggregation;
}

class DateHistogramBucketAggregationConfig {
  field: string;

  interval: string;

  minDocCount: number;

  offset: string;

  constructor(
    field: string = '',
    interval: string = INTERVAL_SELECT_OPTIONS[''][0].value,
    minDocCount: number = 0,
    // trimEdges: number = 0,
    offset: string = '',
    // timezone: string = 'coordinatedUniversalTime',
  ) {
    this.field = field;
    this.interval = interval;
    this.minDocCount = minDocCount;
    this.offset = offset;
  }
}

export class DateHistogramBucketAggregations extends BucketAggregation {
  public config: DateHistogramBucketAggregationConfig;

  constructor(id: UUID, show?: boolean, options?: Partial<DateHistogramBucketAggregationConfig>) {
    super(id, 'dateHistogram', show);
    this.config = new DateHistogramBucketAggregationConfig();
    Object.assign(this.config, options);
  }

  override createClone(id: UUID) {
    const clone = new DateHistogramBucketAggregations(id, this.show, this.config);
    return clone;
  }

  override isValid() {
    return this.config.field.trim() !== '';
  }

  override configure(config: Partial<DateHistogramBucketAggregationConfig>) {
    const newConfig = {
      ...config,
      offset: config.offset ? `${config.offset}` : '',
    };
    Object.assign(this.config, newConfig);
  }

  override changeType(type: BucketAggregationsType) {
    const newBucket = BucketAggregationFactory.createBucketAggregation(type);
    switch (type) {
      case 'histogram':
        newBucket.configure({
          field: this.config.field,
          minDocCount: this.config.minDocCount,
        });
        break;
      case 'terms':
        newBucket.configure({
          field: this.config.field,
          minDocCount: this.config.minDocCount,
        });
        break;
      default:
        break;
    }

    return newBucket;
  }
}

class FilterBucketAggregationConfig {
  queryLabels: {
    query: string;
    label: string;
  }[];

  constructor(
    queryLabels: {
      query: string;
      label: string;
    }[] = [
      {
        query: '*',
        label: '',
      },
    ],
  ) {
    this.queryLabels = queryLabels;
  }
}

export class FiltersBucketAggregations extends BucketAggregation {
  public config: FilterBucketAggregationConfig;

  constructor(id: UUID, show?: boolean, options?: Partial<FilterBucketAggregationConfig>) {
    super(id, 'filters', show);
    this.config = new FilterBucketAggregationConfig();
    Object.assign(this.config, options);
  }

  override createClone(id: UUID): BucketAggregation {
    const clone = new FiltersBucketAggregations(id, this.show, this.config);
    return clone;
  }

  override isValid() {
    return this.config.queryLabels.length > 0 && this.config.queryLabels.at(0)?.query.trim() !== '';
  }

  override configure(config: Partial<FilterBucketAggregationConfig>) {
    Object.assign(this.config, config);
  }

  override changeType(type: BucketAggregationsType) {
    return BucketAggregationFactory.createBucketAggregation(type);
  }
}

class HistogramBucketAggregationConfig {
  field: string;

  interval: number;

  minDocCount: number;

  constructor(field: string = '', interval: number = 1000, minDocCount: number = 0) {
    this.field = field;
    this.interval = interval;
    this.minDocCount = minDocCount;
  }
}

export class HistogramBucketAggregations extends BucketAggregation {
  public config: HistogramBucketAggregationConfig;

  constructor(id: UUID, show?: boolean, options?: Partial<HistogramBucketAggregationConfig>) {
    super(id, 'histogram', show);
    this.config = new HistogramBucketAggregationConfig();
    Object.assign(this.config, options);
  }

  override createClone(id: UUID): BucketAggregation {
    const clone = new HistogramBucketAggregations(id, this.show, this.config);
    return clone;
  }

  override isValid() {
    return this.config.field.trim() !== '';
  }

  override configure(config: Partial<HistogramBucketAggregationConfig>) {
    Object.assign(this.config, config);
  }

  override changeType(type: BucketAggregationsType) {
    const newBucket = BucketAggregationFactory.createBucketAggregation(type);
    switch (type) {
      case 'dateHistogram':
        newBucket.configure({
          field: this.config.field,
          minDocCount: this.config.minDocCount,
        });
        break;
      case 'terms':
        newBucket.configure({
          field: this.config.field,
          minDocCount: this.config.minDocCount,
        });
        break;
      default:
        break;
    }

    return newBucket;
  }
}

class TermsBucketAggregationConfig {
  field: string;

  order: string;

  size: number;

  minDocCount: number;

  orderBy: string;

  missing: string;

  constructor(
    field: string = '',
    order: string = 'asc',
    size: number = 10,
    minDocCount: number = 0,
    orderBy: string = '_term',
    missing: string = '',
  ) {
    this.field = field;
    this.order = order;
    this.size = size;
    this.minDocCount = minDocCount;
    this.orderBy = orderBy;
    this.missing = missing;
  }
}

export class TermsBucketAggregations extends BucketAggregation {
  public config: TermsBucketAggregationConfig;

  constructor(id: UUID, show?: boolean, options?: Partial<TermsBucketAggregationConfig>) {
    super(id, 'terms', show);
    this.config = new TermsBucketAggregationConfig();
    Object.assign(this.config, options);
  }

  override createClone(id: UUID): BucketAggregation {
    const clone = new TermsBucketAggregations(id, this.show, this.config);
    return clone;
  }

  override isValid() {
    return this.config.field.trim() !== '';
  }

  override configure(config: Partial<TermsBucketAggregationConfig>) {
    Object.assign(this.config, config);
    if (this.config.order === 'top') {
      this.config.order = 'asc';
    } else if (this.config.order === 'bottom') {
      this.config.order = 'desc';
    }
  }

  override changeType(type: BucketAggregationsType) {
    const newBucket = BucketAggregationFactory.createBucketAggregation(type);
    switch (type) {
      case 'dateHistogram':
        newBucket.configure({
          field: this.config.field,
          minDocCount: this.config.minDocCount,
        });
        break;
      case 'histogram':
        newBucket.configure({
          field: this.config.field,
          minDocCount: this.config.minDocCount,
        });
        break;
      default:
        break;
    }

    return newBucket;
  }
}
